/**
 * SPA-side authentication context.
 *
 * Wraps the new `@cortex/domain` `/auth/me` + `/auth/logout` surface
 * (PR #4b) and exposes a single hook the rest of the React tree uses
 * to:
 *
 *   - know whether the user is authenticated yet (we boot in
 *     `loading` while we ask the API who the cookie / JWT belongs to);
 *   - read the authenticated principal (`useAuth().principal`);
 *   - check a permission (`useAuth().hasPermission('quotes.create')`);
 *   - sign out (`useAuth().logout()`).
 *
 * State machine:
 *
 *   idle          → about to call /auth/me on mount
 *   loading       → call in flight
 *   authenticated → principal resolved; UI renders the app shell
 *   anonymous     → 401 from /auth/me; UI shows the CloudflareLoginScreen
 *   unconfigured  → no VITE_API_BASE_URL; UI falls back to the legacy mock LoginScreen
 *   error         → unexpected network failure; UI shows a retry banner
 *
 * The mock-mode path lives in App.tsx (it pre-dates this context and
 * still satisfies a couple of UX flows we have not migrated). When
 * `state === 'unconfigured'`, callers should render the legacy mock
 * login UI instead of pulling principals from this context.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { AuthMeResponse } from '@cortex/domain';
import { isApiClientError } from '@cortex/domain/client';

import { cortexApi, isApiConfigured } from '../services/cortexApi';
import type { UserRole } from '../types';
import { deriveLegacyRole, isInternalEntity } from './roleAdapter';

export type AuthPrincipal = AuthMeResponse & {
  /** Derived for the legacy SPA layout (sidebar, profile chip). */
  readonly legacyRole: UserRole;
  /** True when the principal belongs to an Insurer entity. */
  readonly isInternal: boolean;
};

export type AuthState =
  | { readonly status: 'loading' }
  | { readonly status: 'authenticated'; readonly principal: AuthPrincipal }
  | { readonly status: 'anonymous' }
  | { readonly status: 'unconfigured' }
  | { readonly status: 'error'; readonly message: string };

export interface AuthContextValue {
  readonly state: AuthState;
  /** Convenience accessor — `null` unless `state.status === 'authenticated'`. */
  readonly principal: AuthPrincipal | null;
  /** True iff the principal currently holds the named permission key. */
  hasPermission(key: string): boolean;
  /**
   * Trigger a /auth/logout call and redirect to the Cloudflare Access
   * logout URL the API returns. In `unconfigured` mode this resolves
   * locally (no network) so the legacy mock flow still completes.
   */
  logout(): Promise<void>;
  /** Re-fetch /auth/me (e.g. after the user re-authenticates via CF Access). */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const toPrincipal = (me: AuthMeResponse): AuthPrincipal => ({
  ...me,
  legacyRole: deriveLegacyRole(me.permissions.roles),
  isInternal: isInternalEntity(me.user.entity.typeSlug),
});

interface AuthProviderProps {
  readonly children: React.ReactNode;
  /**
   * Test seam — when supplied, skips the real API call and starts in
   * the given state. Production never passes this.
   */
  readonly initialState?: AuthState;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, initialState }) => {
  const [state, setState] = useState<AuthState>(
    initialState ?? (isApiConfigured() ? { status: 'loading' } : { status: 'unconfigured' }),
  );

  // `refresh` is stable so consumers can put it in effect deps without
  // re-triggering. The ref keeps the latest `setState` accessible
  // without re-creating the callback identity.
  const setStateRef = useRef(setState);
  setStateRef.current = setState;

  const refresh = useCallback(async (): Promise<void> => {
    if (!isApiConfigured()) {
      setStateRef.current({ status: 'unconfigured' });
      return;
    }

    setStateRef.current({ status: 'loading' });

    try {
      const me = await cortexApi.auth.me();
      setStateRef.current({ status: 'authenticated', principal: toPrincipal(me) });
    } catch (err) {
      if (isApiClientError(err)) {
        if (err.code === 'unauthorized' || err.status === 401) {
          setStateRef.current({ status: 'anonymous' });
          return;
        }
        setStateRef.current({
          status: 'error',
          message: `Could not resolve identity (${err.code}). Try again or contact support.`,
        });
        return;
      }
      setStateRef.current({
        status: 'error',
        message: 'Could not reach the CORTEX API. Check your connection and try again.',
      });
    }
  }, []);

  useEffect(() => {
    if (initialState) return; // tests own the state machine
    if (!isApiConfigured()) return;
    void refresh();
  }, [initialState, refresh]);

  const hasPermission = useCallback(
    (key: string): boolean => {
      if (state.status !== 'authenticated') return false;
      return state.principal.permissions.keys.includes(key);
    },
    [state],
  );

  const logout = useCallback(async (): Promise<void> => {
    if (state.status !== 'authenticated') {
      // Mock / unconfigured fallback — just collapse to anonymous so
      // App.tsx's existing mock flow still surfaces the LoginScreen.
      setStateRef.current({ status: 'anonymous' });
      return;
    }

    try {
      const result = await cortexApi.auth.logout();
      setStateRef.current({ status: 'anonymous' });
      // Hard-redirect so Cloudflare Access can clear its own session
      // cookie. The next request lands back on the SPA, which boots in
      // `loading` and resolves to `anonymous` until the user re-auths.
      window.location.assign(result.cfLogoutUrl);
    } catch {
      // Even if /auth/logout fails (network, expired session), we lock
      // the local UI out — better to over-sign-out than to leave the
      // user with a stale principal in memory.
      setStateRef.current({ status: 'anonymous' });
    }
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      principal: state.status === 'authenticated' ? state.principal : null,
      hasPermission,
      logout,
      refresh,
    }),
    [state, hasPermission, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>.');
  }
  return ctx;
};

/** Hook alias when you only need a permission check. */
export const useHasPermission = (key: string): boolean => useAuth().hasPermission(key);
